import { useTranslation } from "react-i18next";

import { ControlOptions } from "leaflet";
import {
  IconButton,
  Paper,
  Portal,
  styled,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowLeft, ArrowRight, ManageSearch } from "@mui/icons-material";
import { ErrorBoundary } from "react-error-boundary";

import { useIndicators } from "hooks/indicatorHooks";
import { useSearchSelected } from "hooks/searchHooks";
import { useMapPoint } from "hooks/mapHooks";

import { LayerProperties } from "types";

import { MapControlBase } from "./MapControlBase";
import { InlineDrawer } from "components/drawers/InlineDrawer";
import { ActiveFab } from "components/button/ActiveFab";
import { LayerSearchForm } from "components/form/layer/LayerSearchForm";
import {
  MapPanelEvent,
  MapPanelState,
  MapPanelStateSearch,
} from "machines/MapPanelMachine";

interface SearchControlProps extends ControlOptions {
  config: string;
  layerProperties: LayerProperties[];
  drawerContainer: Element;
  stateValue: MapPanelState;
  updateState: (value: MapPanelEvent["type"]) => void;
}

function isMapStateSearch(data: MapPanelState): data is MapPanelStateSearch {
  return data.hasOwnProperty("search");
}

export function LayerSearchControl(props: SearchControlProps) {
  const {
    stateValue,
    updateState,
    drawerContainer,
    layerProperties,
    config,
    ...controlProps
  } = props;
  const { t } = useTranslation();
  const [point] = useMapPoint();
  const [{ selected }] = useSearchSelected();
  const { data: layers } = useIndicators(
    config,
    {
      limit: 20,
      offset: 0,
      searchable: true,
    },
    layerProperties
  );

  return (
    <MapControlBase {...controlProps}>
      {layers && layers.totalCount > 0 && (
        <Tooltip title={<>{t("label.searchInLayers")}</>} placement="left">
          <ActiveFab
            className={isMapStateSearch(stateValue) ? "active" : "inactive"}
            size="small"
            variant="circular"
            onClick={() =>
              updateState(
                isMapStateSearch(stateValue) ? "CLOSING" : "MODE_SEARCH"
              )
            }
          >
            <ManageSearch />
          </ActiveFab>
        </Tooltip>
      )}
      <Portal container={drawerContainer}>
        {isMapStateSearch(stateValue) &&
          stateValue.search === "searchCollapsed" && (
            <StyledFloatingIconButton
              className="has-point-open-button"
              size="small"
              onClick={() => updateState("OPENING_SEARCH")}
            >
              <ArrowLeft />
            </StyledFloatingIconButton>
          )}
        <StyledInlineDrawer
          open={
            isMapStateSearch(stateValue) && stateValue.search === "searchOpen"
          }
          variant="persistent"
          anchor="right"
        >
          <Paper elevation={3}>
            <Toolbar>
              <Typography variant="subtitle2">
                {t("label.searchInLayers")}
              </Typography>
              <IconButton
                edge="end"
                size="small"
                onClick={() => updateState("CLOSING_SEARCH")}
              >
                <ArrowRight />
              </IconButton>
            </Toolbar>
          </Paper>
          {config && (
            <ErrorBoundary
              fallbackRender={() => {
                updateState("CLOSING_SEARCH");
                return null;
              }}
            >
              <LayerSearchForm
                className={
                  selected &&
                  !point &&
                  isMapStateSearch(stateValue) &&
                  stateValue.search === "searchCollapsed"
                    ? "content-hidden grow"
                    : "grow"
                }
                config={config}
                layerProperties={layerProperties}
              />
            </ErrorBoundary>
          )}
        </StyledInlineDrawer>
      </Portal>
    </MapControlBase>
  );
}

const StyledFloatingIconButton = styled(IconButton)`
  position: absolute;
  top: 20px;
  left: -10px;
  z-index: 2;
  justify-self: flex-start;
  width: 20px;
  height: 20px;
  color: #000;
  background-color: #fff;

  &:hover {
    background-color: #ccc;
  }
`;

const StyledInlineDrawer = styled(InlineDrawer)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  .content-hidden {
    & > * {
      visibility: hidden;
    }
  }

  .MuiPaper-elevation {
    background-image: unset;
    z-index: 1;
  }

  .MuiToolbar-root {
    display: flex;

    .MuiIconButton-root {
      margin-left: auto;
    }
  }

  .grow {
    flex: 1 1 100%;
    z-index: 0;
  }
`;
